import { createContext, useContext, useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { API_BASE_URL } from '../config/api'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// --- Helper Functions ---
const isGuestUser = (session) => {
  if (!session?.user) return false
  return session.user.email?.includes('@guest.com') || session.user.user_metadata?.isGuest
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isGuest, setIsGuest] = useState(false)
  const [showRegister, setShowRegister] = useState(false)

  const pendingProfileFetch = useRef(null)

  const fetchProfile = useCallback(async (sessionToken) => {
    if (!sessionToken) return
    
    // Deduplication
    if (pendingProfileFetch.current) return pendingProfileFetch.current

    const promise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/profile`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          setProfile(data)
          return data
        }
      } catch (error) {
        console.error('Error fetching profile:', error)
      } finally {
        pendingProfileFetch.current = null
      }
    })()

    pendingProfileFetch.current = promise
    return promise
  }, [])

  // Consolidate Session Update Logic
  const handleSessionUpdate = useCallback((newSession) => {
    setSession(newSession)
    setUser(newSession?.user ?? null)
    setIsGuest(isGuestUser(newSession))
    
    if (newSession?.access_token) {
      fetchProfile(newSession.access_token)
    } else {
      setProfile(null)
    }
  }, [fetchProfile])

  useEffect(() => {
    // Initial Load
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      handleSessionUpdate(initialSession)
      setLoading(false)
    })

    // Subscription
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      handleSessionUpdate(currentSession)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [handleSessionUpdate])

  // --- Auth Actions ---

  const signUp = useCallback(async (email, password, username) => {
    return await supabase.auth.signUp({
      email,
      password,
      options: { data: { username: username?.trim() || '' } }
    })
  }, [])

  const signIn = useCallback(async (email, password) => {
    return await supabase.auth.signInWithPassword({ email, password })
  }, [])

  const resetPassword = useCallback(async (email) => {
    return await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
  }, [])

  const updatePassword = useCallback(async (newPassword) => {
    return await supabase.auth.updateUser({ password: newPassword })
  }, [])

  const signInAsGuest = useCallback(async () => {
    setLoading(true)
    try {
      const guestId = Date.now()
      const guestEmail = `guest${guestId}@guest.com`
      const guestPassword = `guest_password_${guestId}`

      // Create guest
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: guestEmail,
        password: guestPassword,
        options: {
          data: {
            username: `Guest_${Math.random().toString(36).substring(2, 7)}`,
            isGuest: true
          }
        }
      })

      if (signUpError) return { error: signUpError }

      // Auto-signin only if session missing (email confirmation disabled for guests usually)
      if (!signUpData.session) {
         const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: guestEmail,
            password: guestPassword
         })
         if (signInError) return { error: signInError }
         // Session set by onAuthStateChange listener
         return { data: signInData }
      }
      
      return { data: signUpData }

    } catch (error) {
      console.error('Guest login error:', error)
      return { error }
    } finally {
      setLoading(false)
    }
  }, [])

  const signOut = useCallback(async () => {
    // Attempt to leave queue (best effort)
    if (session?.access_token) {
        try {
            await fetch(`${API_BASE_URL}/api/v1/games/queue`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${session.access_token}` }
            })
        } catch (error) {
            console.warn('Failed to leave queue during signout:', error)
        }
    }

    setIsGuest(false)
    setProfile(null)
    return await supabase.auth.signOut()
  }, [session?.access_token])

  const triggerRegistration = useCallback(async () => {
    setShowRegister(true)
    if (user) await signOut()
  }, [user, signOut])

  const resendVerificationEmail = useCallback(async (redirectUrl) => {
      if (!user?.email) return { error: 'No email found' }
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/auth/resend-verification`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': session?.access_token ? `Bearer ${session.access_token}` : undefined
          },
          body: JSON.stringify({ email: user.email, redirect_url: redirectUrl })
        })
        
        if (!response.ok) {
           const error = await response.json()
           return { error: error.error || 'Failed to send verification email' }
        }
        return { error: null }
      } catch (error) {
        return { error: error.message }
      }
  }, [user?.email, session?.access_token])

  const value = useMemo(() => ({
    user,
    profile,
    session,
    loading,
    isGuest,
    showRegister,
    setShowRegister,
    signUp,
    signIn,
    signInAsGuest,
    signOut,
    triggerRegistration,
    resetPassword,
    updatePassword,
    isVerified: profile?.email_verified ?? false,
    resendVerificationEmail
  }), [
    user, profile, session, loading, isGuest, showRegister,
    signUp, signIn, signInAsGuest, signOut, triggerRegistration,
    resetPassword, updatePassword, resendVerificationEmail
  ])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
